import { useState, useEffect } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { BRAND } from "../../data/content";
import { toast } from "sonner";
import { updateCollectionVersion } from "../../services/versionService";
import { Save, Loader2, Store } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";

// ---------------------------------------------------------------------------
// Default values from content.js
// ---------------------------------------------------------------------------
const DEFAULTS = {
  phone: BRAND.phone,
  whatsapp: BRAND.whatsapp,
  email: BRAND.email,
  address: BRAND.address,
  instagram: BRAND.instagram,
  tiktok: BRAND.tiktok,
  facebook: BRAND.facebook,
  socials: {
    instagram: BRAND.socials.instagram,
    tiktok: BRAND.socials.tiktok,
    facebook: BRAND.socials.facebook,
  },
};

// ---------------------------------------------------------------------------
// Field group definitions
// ---------------------------------------------------------------------------
const CONTACT_FIELDS = [
  {
    id: "phone",
    label: "Telepon",
    type: "tel",
    placeholder: "+62 812 8032 7569",
    social: false,
  },
  {
    id: "whatsapp",
    label: "WhatsApp Number",
    type: "text",
    placeholder: "6281280327569",
    hint: "Hanya angka, dengan kode negara (tanpa +)",
    social: false,
  },
  {
    id: "email",
    label: "Email",
    type: "email",
    placeholder: "lw5051900@gmail.com",
    social: false,
  },
  {
    id: "address",
    label: "Alamat",
    type: "textarea",
    placeholder: "Perum Telaga Pesona ...",
    social: false,
  },
];

const SOCIAL_FIELDS = [
  {
    id: "instagram",
    label: "Instagram Handle",
    type: "text",
    placeholder: "@sanggar_rias_adibah",
    social: false,
  },
  {
    id: "tiktok",
    label: "TikTok Handle",
    type: "text",
    placeholder: "@lina.wati4399",
    social: false,
  },
  {
    id: "facebook",
    label: "Facebook Name",
    type: "text",
    placeholder: "lina mua cikarang",
    social: false,
  },
];

const LINK_FIELDS = [
  {
    id: "socials.instagram",
    label: "Instagram URL",
    type: "url",
    placeholder: "https://www.instagram.com/sanggar_rias_adibah",
    key: "instagram",
  },
  {
    id: "socials.tiktok",
    label: "TikTok URL",
    type: "url",
    placeholder: "https://www.tiktok.com/@lina.wati4399",
    key: "tiktok",
  },
  {
    id: "socials.facebook",
    label: "Facebook URL",
    type: "url",
    placeholder: "https://web.facebook.com/moms.adibah",
    key: "facebook",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, part) => (acc ? acc[part] : ""), obj);
}

function setNestedValue(obj, path, value) {
  const keys = path.split(".");
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [first, ...rest] = keys;
  return {
    ...obj,
    [first]: setNestedValue(obj[first] || {}, rest.join("."), value),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function BrandEditor() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Fetch existing data on mount
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, "brand", "site"));
        if (mounted && snap.exists()) {
          const data = snap.data();
          setForm({
            phone: data.phone ?? DEFAULTS.phone,
            whatsapp: data.whatsapp ?? DEFAULTS.whatsapp,
            email: data.email ?? DEFAULTS.email,
            address: data.address ?? DEFAULTS.address,
            instagram: data.instagram ?? DEFAULTS.instagram,
            tiktok: data.tiktok ?? DEFAULTS.tiktok,
            facebook: data.facebook ?? DEFAULTS.facebook,
            socials: {
              instagram:
                data.socials?.instagram ?? DEFAULTS.socials.instagram,
              tiktok: data.socials?.tiktok ?? DEFAULTS.socials.tiktok,
              facebook:
                data.socials?.facebook ?? DEFAULTS.socials.facebook,
            },
          });
        }
      } catch (err) {
        console.warn("Brand fetch error, using defaults:", err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, []);

  // Handle field changes
  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = setNestedValue(prev, field, value);
      return next;
    });
    setDirty(true);
  };

  // Handle social link changes
  const handleSocialChange = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      socials: { ...prev.socials, [key]: e.target.value },
    }));
    setDirty(true);
  };

  // --------------- Save ---------------
  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, "brand", "site"),
        {
          phone: form.phone,
          whatsapp: form.whatsapp,
          email: form.email,
          address: form.address,
          instagram: form.instagram,
          tiktok: form.tiktok,
          facebook: form.facebook,
          socials: {
            instagram: form.socials.instagram,
            tiktok: form.socials.tiktok,
            facebook: form.socials.facebook,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      toast.success("Brand & contact saved successfully");
      await updateCollectionVersion("brand");
      setDirty(false);
    } catch (err) {
      toast.error("Failed to save brand data: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // --------------- Render helpers ---------------
  const renderField = (field) => {
    if (field.type === "textarea") {
      return (
        <div key={field.id}>
          <Label
            htmlFor={field.id}
            className="text-sm font-medium text-[#fdfbf7] mb-1.5 block"
          >
            {field.label}
          </Label>
          <Textarea
            id={field.id}
            value={form[field.id] || ""}
            onChange={handleChange(field.id)}
            placeholder={field.placeholder}
            className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50 min-h-[80px] resize-y"
          />
        </div>
      );
    }

    return (
      <div key={field.id}>
        <Label
          htmlFor={field.id}
          className="text-sm font-medium text-[#fdfbf7] mb-1.5 block"
        >
          {field.label}
        </Label>
        <Input
          id={field.id}
          type={field.type}
          value={form[field.id] || ""}
          onChange={handleChange(field.id)}
          placeholder={field.placeholder}
          className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
        />
        {field.hint && (
          <p className="text-[11px] text-[#a6a196] mt-1">{field.hint}</p>
        )}
      </div>
    );
  };

  const renderSocialField = (field) => {
    return (
      <div key={field.id}>
        <Label
          htmlFor={field.id}
          className="text-sm font-medium text-[#fdfbf7] mb-1.5 block"
        >
          {field.label}
        </Label>
        <Input
          id={field.id}
          type={field.type}
          value={form[field.id] || ""}
          onChange={handleChange(field.id)}
          placeholder={field.placeholder}
          className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
        />
      </div>
    );
  };

  const renderLinkField = (field) => {
    const value = form.socials?.[field.key] || "";
    return (
      <div key={field.id}>
        <Label
          htmlFor={field.id}
          className="text-sm font-medium text-[#fdfbf7] mb-1.5 block"
        >
          {field.label}
        </Label>
        <Input
          id={field.id}
          type={field.type}
          value={value}
          onChange={handleSocialChange(field.key)}
          placeholder={field.placeholder}
          className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
        />
      </div>
    );
  };

  // --------------- Loading skeleton ---------------
  if (loading) {
    return (
      <div className="space-y-8 max-w-3xl mx-auto">
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-8 w-48 bg-[#15120f] rounded border border-[#d4af37]/10 animate-pulse"
            />
          ))}
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-12 bg-[#15120f] rounded-lg border border-[#d4af37]/10 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  // --------------- Main render ---------------
  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
          <Store className="w-5 h-5 text-[#d4af37]" />
          <h1 className="text-2xl font-display text-[#fdfbf7]">Brand & Kontak</h1>
        </div>
        <p className="text-sm text-[#a6a196] ml-4">
          Kelola informasi kontak dan sosial media. Nama brand, tagline, dan
          logo bersifat statis.
        </p>
      </div>

      {/* Static info notice */}
      <Card className="bg-[#0a0908] border-[#d4af37]/15">
        <CardContent className="pt-4 pb-4 px-5">
          <div className="flex items-center gap-3 text-sm text-[#a6a196]">
            <div className="w-2 h-2 rounded-full bg-[#d4af37]" />
            <p>
              <span className="text-[#fdfbf7] font-medium">Brand Name</span>,{" "}
              <span className="text-[#fdfbf7] font-medium">Tagline</span>,{" "}
              <span className="text-[#fdfbf7] font-medium">Logo</span>, and{" "}
              <span className="text-[#fdfbf7] font-medium">Founded Year</span>{" "}
              dikelola di dalam kode (penting untuk SEO).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card className="bg-[#0a0908] border-[#d4af37]/20">
        <CardHeader>
          <CardTitle className="text-[#fdfbf7] text-lg font-display">
            Kontak
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {CONTACT_FIELDS.map(renderField)}
        </CardContent>
      </Card>

      {/* Social Media Handles */}
      <Card className="bg-[#0a0908] border-[#d4af37]/20">
        <CardHeader>
          <CardTitle className="text-[#fdfbf7] text-lg font-display">
            Social Media
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {SOCIAL_FIELDS.map(renderSocialField)}
        </CardContent>
      </Card>

      {/* Social Links */}
      <Card className="bg-[#0a0908] border-[#d4af37]/20">
        <CardHeader>
          <CardTitle className="text-[#fdfbf7] text-lg font-display">
            Links
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {LINK_FIELDS.map(renderLinkField)}
        </CardContent>
      </Card>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-[#0a0908]/95 backdrop-blur-sm border border-[#d4af37]/10 rounded-lg p-4 flex items-center justify-between">
        <p className="text-xs text-[#a6a196]">
          {dirty
            ? "Ada perubahan yang belum disimpan"
            : "Semua perubahan tersimpan"}
        </p>
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="bg-[#d4af37] hover:bg-[#c49f2e] text-[#0a0908] font-medium text-sm disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Menyimpan...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Simpan Perubahan
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
